import React, { useState, useRef, useEffect } from 'react'
import { SendOutlined, RobotOutlined, UserOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Drawer, Input, Button, Space, Typography, Spin, message, Divider, Avatar, Collapse } from 'antd'
import { useTranslation } from 'react-i18next'

const { TextArea } = Input
const { Text, Paragraph } = Typography
const PROMOTO_TEXT = ['workflows.ai_assistant.examples.list_products', 'workflows.ai_assistant.examples.low_stock', 'workflows.ai_assistant.examples.order_stats']

/**
 * AIAssistantDrawer - AI Assistant for Workflow Script Generation
 */
export default function AIAssistantDrawer({
  visible,
  onClose,
  currentScript,
  currentUiConfig,
  processing = false,
  onSendMessage
}) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [chatHistory, setChatHistory] = useState([])
  const chatContainerRef = useRef(null)

  // Auto scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatHistory])

  // Welcome message when drawer opens
  useEffect(() => {
    if (visible && chatHistory.length === 0) {
      setChatHistory([{
        role: 'assistant',
        content: t('workflows.ai_assistant.welcome_message'),
        timestamp: new Date()
      }])
    }
  }, [visible, t])

  const handleSend = async () => {
    const trimmedInput = inputValue.trim()
    if (!trimmedInput) {
      message.warning(t('workflows.ai_assistant.empty_input'))
      return
    }

    const userMessage = {
      role: 'user',
      content: trimmedInput,
      timestamp: new Date()
    }

    setChatHistory(prev => [...prev, userMessage])
    setInputValue('')

    if (onSendMessage) {
      try {
        const aiResponse = await onSendMessage(trimmedInput, chatHistory)
        if (aiResponse) {
          setChatHistory(prev => [...prev, {
            role: 'assistant',
            content: aiResponse,
            timestamp: new Date()
          }])
        }
      } catch (error) {
        console.error('AI Assistant error:', error)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Drawer
      title={
        <Space>
          <RobotOutlined style={{ color: '#1890ff' }} />
          <Text strong>{t('workflows.ai_assistant.title')}</Text>
        </Space>
      }
      placement="right"
      width={500}
      onClose={onClose}
      open={visible}
      styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0 } }}
    >
      {/* Info Banner */}
      <div style={{ padding: '12px 16px', backgroundColor: '#f0f5ff', borderBottom: '1px solid #d9d9d9' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <Text strong>{t('workflows.ai_assistant.info_title')}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('workflows.ai_assistant.info_description')}
          </Text>
        </Space>

        {/* Example Prompts */}
        <Collapse
          ghost
          size="small"
          style={{ marginTop: 8 }}
          items={[{
            key: '1',
            label: <Text type="secondary" style={{ fontSize: '12px' }}>{t('workflows.ai_assistant.example_prompts')}</Text>,
            children: (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {PROMOTO_TEXT.map((prompt, idx) => (
                  <Button
                    key={idx}
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', textAlign: 'left', whiteSpace: 'normal' }}
                    onClick={() => setInputValue(t(prompt))}
                  >
                    {t(prompt)}
                  </Button>
                ))}
              </Space>
            )
          }]}
        />
      </div>

      {/* Chat History */}
      <div
        ref={chatContainerRef}
        style={{ flex: 1, overflowY: 'auto', padding: '16px', backgroundColor: '#fafafa' }}
      >
        {chatHistory.map((msg, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              marginBottom: '16px',
              alignItems: 'flex-start',
              flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
            }}
          >
            <Avatar
              icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
              style={{
                backgroundColor: msg.role === 'user' ? '#52c41a' : '#1890ff',
                flexShrink: 0,
                marginLeft: msg.role === 'user' ? '8px' : '0',
                marginRight: msg.role === 'user' ? '0' : '8px'
              }}
            />
            <div
              style={{
                maxWidth: '80%',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: msg.role === 'user' ? '#e6f7ff' : '#ffffff',
                border: msg.role === 'user' ? '1px solid #91d5ff' : '1px solid #d9d9d9',
                wordWrap: 'break-word'
              }}
            >
              <Paragraph style={{ marginBottom: 0, fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                {msg.content}
              </Paragraph>
              <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                {msg.timestamp?.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </div>
          </div>
        ))}

        {processing && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Space>
              <Spin size="small" />
              <Text type="secondary">{t('workflows.ai_assistant.thinking')}</Text>
            </Space>
          </div>
        )}
      </div>

      <Divider style={{ margin: 0 }} />

      {/* Input Area */}
      <div style={{ padding: '16px', backgroundColor: '#ffffff', borderTop: '1px solid #d9d9d9' }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('workflows.ai_assistant.input_placeholder')}
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={processing}
            style={{ resize: 'none' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={processing}
            disabled={!inputValue.trim()}
            style={{ height: 'auto', alignSelf: 'flex-end' }}
          >
            {t('common.button.send')}
          </Button>
        </Space.Compact>
      </div>
    </Drawer>
  )
}
